import { pdfClient } from "../clients/pdf.client";
import type { ReportDocumentProps } from "../../hooks/useReportPDF";
import { REPORT_CONSTANTS } from "../../config/constants";
import { extractCombinedData } from "../../helpers";
import { parseFilenameFromContentDisposition } from "~/helpers/parse-content-disposition.helper";

export interface GeneratedDocument {
  url: string;
  filename?: string;
}

interface SubmitPdfJobResponse {
  requestId: string;
}

interface PdfJobStatusResponse {
  status: "pending" | "completed" | "error";
  detail?: string;
}

// The backend enqueues and replies 202 immediately rather than blocking on
// pdf-service, so this polls in its place. No shared resource is held open
// while polling, so the max wait can stay generous.
const PDF_POLL_INTERVAL_MS = 3000;
const PDF_POLL_MAX_WAIT_MS = 20 * 60 * 1000;

class PDFGateway {
  private async pollUntilReady(requestId: string): Promise<void> {
    const deadline = Date.now() + PDF_POLL_MAX_WAIT_MS;
    while (Date.now() < deadline) {
      const { data } = await pdfClient.get<PdfJobStatusResponse>(`/pdf/status/${requestId}`);
      if (data.status === 'completed') return;
      if (data.status === 'error') throw new Error(data.detail || 'Falha ao gerar PDF.');
      await new Promise((resolve) => setTimeout(resolve, PDF_POLL_INTERVAL_MS));
    }
    throw new Error('Tempo limite excedido aguardando a geração do PDF.');
  }

  async generatePDF(props: ReportDocumentProps): Promise<GeneratedDocument> {
    try {
      const reportType = props.metadata?.[REPORT_CONSTANTS.new_report.report_type];
      const isCombinado = reportType === REPORT_CONSTANTS.types.combinado;
      const basePayload = isCombinado ? extractCombinedData(props) : props; // Caso seja combinado, filtrar para enviar apenas combined_data
      const pdfPayload: ReportDocumentProps = {
        ...basePayload,
        ...(props.executive_summary !== undefined
          ? { executive_summary: props.executive_summary }
          : {}),
      };

      const { data: job } = await pdfClient.post<SubmitPdfJobResponse>('/create/pdf', pdfPayload);
      await this.pollUntilReady(job.requestId);

      const response = await pdfClient.get(`/pdf/download/${job.requestId}`, {
        responseType: 'blob'
      });

      const pdfBlob = response.data;
      const url = URL.createObjectURL(pdfBlob);
      const filename = parseFilenameFromContentDisposition(
        response.headers?.['content-disposition'] ?? response.headers?.['Content-Disposition'],
      );

      console.log('PDF generated successfully by service');
      return { url, filename };
    } catch (error) {
      console.error('Error calling PDF service:', error);
      throw error;
    }
  }

  onProgress(enabled: boolean): void {
    // No-op for service-based implementation
    console.log('PDF service progress logging:', enabled);
  }
}

export const pdfGateway = new PDFGateway();
pdfGateway.onProgress(true);
